/**
 * Browser notification utilities for study reminders
 */

// Check if browser supports notifications
export const isNotificationSupported = () => {
  return 'Notification' in window;
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return { success: false, permission: 'unsupported' };
  }

  try {
    const permission = await Notification.requestPermission();
    return { success: permission === 'granted', permission };
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return { success: false, permission: 'error' };
  }
};

// Show a notification
export const showNotification = (title, options = {}) => {
  if (!isNotificationSupported()) return null;
  if (Notification.permission !== 'granted') return null;

  try {
    const notification = new Notification(title, {
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      tag: 'flashcard-reminder',
      renotify: true,
      ...options
    });

    // Auto close after 10 seconds
    setTimeout(() => notification.close(), 10000);

    return notification;
  } catch (error) {
    console.error('Error showing notification:', error);
    return null;
  }
};

// Notification types for the flashcard app
export const notifications = {
  // Streak at risk reminder
  streakAtRisk: (streakDays, hoursRemaining) => {
    return showNotification(
      `Your ${streakDays}-day streak is at risk!`,
      {
        body: `Complete at least one card in the next ${hoursRemaining} hours to keep your streak alive.`,
        tag: 'streak-at-risk',
        requireInteraction: true
      }
    );
  },

  // Cards due reminder
  cardsDue: (count) => {
    return showNotification(
      `${count} cards due today`,
      {
        body: 'Take a few minutes to review your flashcards.',
        tag: 'cards-due'
      }
    );
  },

  // Haven't studied today
  noActivityToday: (streakDays) => {
    const message = streakDays > 0
      ? `You haven't studied today. Don't lose your ${streakDays}-day streak!`
      : "You haven't studied today. Start your streak now!";

    return showNotification(
      'Time to study!',
      {
        body: message,
        tag: 'no-activity'
      }
    );
  },

  // Daily goal reminder
  dailyGoalProgress: (completed, goal) => {
    const remaining = goal - completed;
    if (remaining <= 0) return null;

    return showNotification(
      `${remaining} cards to reach your daily goal`,
      {
        body: `You've reviewed ${completed} of ${goal} cards today.`,
        tag: 'daily-goal'
      }
    );
  },

  // Achievement unlocked (for background notifications)
  achievementUnlocked: (achievementName) => {
    return showNotification(
      'Achievement Unlocked!',
      {
        body: achievementName,
        tag: 'achievement'
      }
    );
  }
};

// Calculate hours until midnight (end of streak day)
export const getHoursUntilMidnight = () => {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return Math.floor((midnight - now) / (1000 * 60 * 60));
};

// Check if should show streak reminder
export const shouldShowStreakReminder = (currentStreak, lastActiveDate, lastReminderTime) => {
  if (currentStreak === 0) return false;

  const today = new Date().toISOString().split('T')[0];

  // Already studied today
  if (lastActiveDate === today) return false;

  // Check if we've already shown a reminder recently (within last 4 hours)
  if (lastReminderTime) {
    const hoursSinceReminder = (Date.now() - lastReminderTime) / (1000 * 60 * 60);
    if (hoursSinceReminder < 4) return false;
  }

  // Show reminder if less than 6 hours until midnight
  const hoursRemaining = getHoursUntilMidnight();
  return hoursRemaining <= 6;
};

// LocalStorage keys for reminder tracking
const REMINDER_KEYS = {
  LAST_REMINDER_TIME: 'flashcard_last_reminder_time',
  REMINDERS_ENABLED: 'flashcard_reminders_enabled'
};

// Get reminder enabled state
export const getRemindersEnabled = () => {
  const stored = localStorage.getItem(REMINDER_KEYS.REMINDERS_ENABLED);
  return stored === null ? true : stored === 'true';
};

// Set reminder enabled state
export const setRemindersEnabled = (enabled) => {
  localStorage.setItem(REMINDER_KEYS.REMINDERS_ENABLED, String(enabled));
};

// Get last reminder time
export const getLastReminderTime = () => {
  const stored = localStorage.getItem(REMINDER_KEYS.LAST_REMINDER_TIME);
  return stored ? parseInt(stored, 10) : null;
};

// Set last reminder time
export const setLastReminderTime = (time = Date.now()) => {
  localStorage.setItem(REMINDER_KEYS.LAST_REMINDER_TIME, String(time));
};

// Check and show streak reminder if needed
export const checkAndShowStreakReminder = (currentStreak, lastActiveDate) => {
  if (!getRemindersEnabled()) return false;
  if (getNotificationPermission() !== 'granted') return false;

  const lastReminderTime = getLastReminderTime();

  if (shouldShowStreakReminder(currentStreak, lastActiveDate, lastReminderTime)) {
    const hoursRemaining = getHoursUntilMidnight();
    notifications.streakAtRisk(currentStreak, hoursRemaining);
    setLastReminderTime();
    return true;
  }

  return false;
};

export default notifications;
